<template>
  <div class="home-container">
    <!-- 头部和Banner区域 -->
    <HomeHeader :is-banner-fixed="isBannerFixed" />
    <!-- 内容区域 -->
    <div class="content-section">
      <div class="content-layout">
        <div class="content-row">
          <!-- 上方：金刚区 + 分类标签 + 下拉刷新 -->
          <div class="main-wrap">
            <QuickAccessModule />
            <div
              class="tag-list"
              :class="{ 'is-fixed': isBannerFixed }"
              ref="tagListRef"
            >
              <div class="tag-list-container">
                <swiper
                  :slides-per-view="'auto'"
                  :space-between="15"
                  :free-mode="true"
                  :pagination="paginationOptions"
                  :modules="[Pagination]"
                  class="tag-swiper"
                >
                  <swiper-slide
                    v-for="(tag, index) in forumStore.forumSections"
                    :key="index"
                    class="tag-item"
                    :class="{ active: forumStore.currentSection === index }"
                    @click="handleTagClick(index)"
                  >
                    {{ capitalizeFirstLetter(t(`home.tags.${tag.name}`)) }}
                  </swiper-slide>
                </swiper>
                <div class="custom-pagination"></div>
              </div>
            </div>

            <div
              class="pull-refresh-indicator"
              :class="{ 'is-refreshing': isRefreshing }"
              :style="{
                top: `${headerHeight}px`,
                height: pullDistance > 0 ? `${pullDistance}px` : '60px',
              }"
            >
              <div class="spinner" v-if="isRefreshing"></div>
            </div>
          </div>

          <!-- 下方：内容列表 -->
          <div class="content-body">
            <div
              class="content-list transform-transition"
              :style="{ transform: `translateY(${pullDistance}px)` }"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <!-- 定制化模块 -->
              <CustomModule />
              <!-- 排序选择器 -->
              <transition name="fade" mode="out-in">
                <div
                  class="sort-selector"
                  v-if="forumStore.currentThreads.length > 0"
                >
                  <div class="sort-options">
                    <div
                      class="sort-option"
                      :class="{
                        active: forumStore.orderBy === OrderBy.DATELINE,
                      }"
                      @click="handleSortChange(OrderBy.DATELINE)"
                    >
                      <span>{{ t("home.sortByTime") }}</span>
                    </div>
                    <div
                      class="sort-option"
                      :class="{
                        active: forumStore.orderBy === OrderBy.LASTPOST,
                      }"
                      @click="handleSortChange(OrderBy.LASTPOST)"
                    >
                      <span>{{ t("home.sortByLastPost") }}</span>
                    </div>
                  </div>
                </div>
              </transition>
              <!-- 标签选择器 -->
              <transition name="fade" mode="out-in">
                <div
                  class="tag-selector"
                  v-if="forumStore.tagInfo && forumStore.tagInfo.length > 0"
                >
                  <TagList
                    mode="select"
                    :tags="forumStore.tagInfo"
                    :selected-tag-ids="
                      forumStore.selectedTagId ? [forumStore.selectedTagId] : []
                    "
                    @tag-toggle="handleTagToggle"
                  />
                </div>
              </transition>
              <!-- 内容展示区域 -->
              <transition name="fade" mode="out-in">
                <!-- 加载中 -->
                <div
                  v-if="
                    forumStore.loading && forumStore.currentThreads.length === 0
                  "
                  class="loading-wrapper"
                  key="loading"
                >
                  <Loading />
                </div>
                <!-- 没有内容 -->
                <div
                  v-else-if="forumStore.currentThreads.length === 0"
                  class="empty-wrapper"
                  key="empty"
                >
                  <Empty
                    :show-img="forumStore.customModules.length === 0"
                    :tip="
                      forumStore.customModules.length > 0
                        ? t('common.noMore')
                        : t('postDetail.noContent')
                    "
                  />
                </div>
                <!-- 有内容 -->
                <div v-else class="transition-content-wrapper">
                  <div class="thread-list">
                    <ThreadItem
                      v-for="(item, index) in forumStore.currentThreads"
                      :key="item.tid"
                      :post="item"
                      :show-follow="true"
                      @update:post="
                        (updatedPost) => updateThread(index, updatedPost)
                      "
                    />
                  </div>
                  <!-- 加载更多 -->
                  <div v-if="forumStore.loadingMore" class="load-more-loading">
                    <Loading type="text" :text="t('common.loadingMore')" />
                  </div>
                  <div v-else-if="forumStore.hasMore" class="load-more-tip">
                    <span>{{ t("common.loadMore") }}</span>
                  </div>
                  <div v-else class="load-more-end">
                    <span>{{ t("common.noMore") }}</span>
                  </div>
                </div>
              </transition>
              <div ref="loadMoreRef" class="load-more-trigger"></div>
            </div>
          </div>
        </div>
        <HomeSidebar class="home-sidebar-wrap" />
      </div>
    </div>
    <!-- 悬浮发布按钮 -->
    <div class="fab-btn" @click="goToPost">
      <img src="@/assets/add.png" alt="post" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useUserStore, useForumStore, useToastStore } from "@/stores";
import { useRouter } from "vue-router";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper/modules";
import { clickButton } from "@/utils/point";
import { OrderBy } from "@/stores/forum";
import { pageType } from "@/utils/point";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
// 组件导入
// @ts-ignore
import ThreadItem from "@/components/common/ThreadItem.vue";
// @ts-ignore
import Loading from "@/components/common/Loading.vue";
// @ts-ignore
import Empty from "@/components/common/Empty.vue";
// @ts-ignore
import CustomModule from "@/components/home/CustomModule.vue";
// @ts-ignore
import HomeHeader from "@/components/home/HomeHeader.vue";
// @ts-ignore
import QuickAccessModule from "@/components/home/QuickAccessModule.vue";
// @ts-ignore
import TagList from "@/components/common/TagList.vue";
// @ts-ignore
import HomeSidebar from "@/components/home/HomeSidebar.vue";
import { capitalizeFirstLetter } from "@/utils";

const { t } = useI18n();
const userStore = useUserStore();
const forumStore = useForumStore();
const toast = useToastStore();
const paginationOptions = {
  el: ".custom-pagination",
  clickable: true,
  renderBullet(index: number, className: string) {
    return '<span class="' + className + '"></span>';
  },
};

// 懒加载和下拉加载更多
const loadMoreRef = ref<HTMLElement | null>(null);
const observer = ref<IntersectionObserver | null>(null);
const tagListRef = ref<HTMLElement | null>(null);

// 计算头部高度（QuickAccessModule + tag-list）
const headerHeight = computed(() => {
  if (!tagListRef.value) return 0;
  const quickAccessModule = forumStore.customModules?.find(
    (module) => module.module_type === "3"
  );
  const quickAccessHeight =
    quickAccessModule?.article_info?.length > 0 ? 80 : 0; // QuickAccessModule 大约高度
  const tagListHeight = tagListRef.value.offsetHeight || 55;
  return quickAccessHeight + tagListHeight;
});

function loadMore() {
  if (!forumStore.hasMore || forumStore.loading || forumStore.loadingMore) {
    return;
  }
  forumStore.loadMoreThreads();
}

// 初始化IntersectionObserver
function initIntersectionObserver() {
  if (observer.value) {
    observer.value.disconnect();
  }

  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    },
    {
      rootMargin: "100px", // 提前100px触发加载
      threshold: 0.1,
    }
  );

  if (loadMoreRef.value) {
    observer.value.observe(loadMoreRef.value);
  }
}

// 添加滚动监听相关逻辑
const isBannerFixed = ref(false);
const scrollThreshold = 80; // 滚动阈值

// 下拉刷新相关状态
const isRefreshing = ref(false);
const pullDistance = ref(0);
const startY = ref(0);
const currentY = ref(0);
const pullThreshold = 80; // 下拉触发阈值
const maxPullDistance = 120; // 最大下拉距离

const router = useRouter();

onMounted(() => {
  // 初始化IntersectionObserver
  initIntersectionObserver();
  forumStore.getForumInfo();
  // 添加滚动监听
  window.addEventListener("scroll", handleScroll);
});

const handleScroll = () => {
  isBannerFixed.value = window.scrollY > scrollThreshold;
};

// 组件卸载时移除滚动监听和observer
onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  if (observer.value) {
    observer.value.disconnect();
  }
});

// 下拉刷新相关方法
const handleTouchStart = (e: TouchEvent) => {
  // 只有在页面顶部时才允许下拉刷新
  if (window.scrollY > 0) return;

  startY.value = e.touches[0].clientY;
  currentY.value = startY.value;
};

const handleTouchMove = (e: TouchEvent) => {
  // 只有在页面顶部时才允许下拉刷新
  if (window.scrollY > 0) return;

  currentY.value = e.touches[0].clientY;
  const deltaY = currentY.value - startY.value;

  // 只处理向下滑动
  if (deltaY > 0) {
    // 检查是否可以取消事件，避免在滚动过程中阻止默认行为
    if (e.cancelable) {
      e.preventDefault();
    }
    const distance = Math.min(deltaY * 0.5, maxPullDistance);
    pullDistance.value = distance;
  }
};

const handleTouchEnd = async () => {
  if (pullDistance.value > pullThreshold && !isRefreshing.value) {
    // 触发刷新
    await handleRefresh();
  } else {
    // 重置状态
    resetPullState();
  }
};

const handleRefresh = async () => {
  isRefreshing.value = true;
  pullDistance.value = pullThreshold;

  try {
    // 如果选中了标签，刷新标签帖子列表
    if (forumStore.selectedTagId) {
      await forumStore.getPageThreadsByTag(
        forumStore.selectedTagId,
        1,
        10,
        false
      );
    } else {
      // 否则重新获取当前板块的帖子列表
      const currentSection = forumStore.currentSectionInfo;
      if (currentSection) {
        await forumStore.getForumThreads(currentSection.fid, 1, 10, false);
      }
    }
    toast.showToast(t("common.refreshSuccess"), "success", 1000);
  } catch (error) {
    console.error("刷新失败:", error);
    toast.showToast(t("common.refreshFailed"), "error", 1000);
  } finally {
    // 延迟重置状态，让用户看到成功提示
    setTimeout(() => {
      resetPullState();
    }, 1000);
  }
};

const resetPullState = () => {
  pullDistance.value = 0;
  isRefreshing.value = false;
};

const handleTagClick = async (index: number) => {
  clickButton({
    eventType: pageType.HOME,
    eventReason: "版区",
    eventValue: forumStore.forumSections[index].name,
  });
  try {
    await forumStore.switchSection(index);
    // 切换版区后滚动到顶部
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  } catch (error) {
    console.error("switch section failed", error);
  }
};

const handleSortChange = async (orderBy: OrderBy) => {
  try {
    await forumStore.switchOrderBy(orderBy);
    clickButton({
      eventType: pageType.HOME,
      eventReason: "排序",
      eventValue: JSON.stringify({
        orderBy: orderBy,
      }),
    });
  } catch (error) {
    console.error("switch order by failed", error);
  }
};

const handleTagToggle = async (tagId: number | string) => {
  try {
    // 如果点击的是已选中的标签，则取消选择
    const newTagId = forumStore.selectedTagId === tagId ? null : tagId;
    await forumStore.switchTag(newTagId);
    clickButton({
      eventType: pageType.HOME,
      eventReason: "标签筛选",
      eventValue: newTagId ? tagId.toString() : "取消",
    });
  } catch (error) {
    console.error("switch tag failed", error);
  }
};

const goToPost = () => {
  if (!userStore.checkBind()) {
    return;
  }

  clickButton({
    eventType: pageType.HOME,
    eventReason: "发布",
  });
  router.push("/post");
};

// 更新帖子
const updateThread = (index: number, updatedPost: any) => {
  // 更新当前显示的帖子列表
  if (forumStore.selectedTagId) {
    forumStore.pageThreads[index] = updatedPost;
  } else {
    forumStore.forumThreads[index] = updatedPost;
  }
  forumStore.updateThreadFollowStatus(
    updatedPost.authorid,
    updatedPost.is_author_followed
  );
};
</script>

<style lang="scss" scoped>
// 变量定义
$banner-height: 160px;
$banner-fixed-height: 60px;
$primary-color: #dd8632;
$text-color: #333;
$white: #fff;
$shadow-sm: 0 2px 8px rgba(104, 60, 60, 0.1);
$border-radius-lg: 20px;
$border-radius-sm: 12px;
$spacing-base: 12px;
$spacing-sm: 8px;
// PC 端内容区居中：content-row(800) + gap(20) + sidebar(280) = 1100px，居中后内容列左缘 = 50vw - 550px
$pc-content-block-half: 550px;

.home-container {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 60px;
  position: relative;
}

.content-section {
  position: relative;
  top: $banner-height;
  background: #f4f6fa;
  border-radius: $border-radius-lg $border-radius-lg 0 0;
  min-height: calc(100vh - 180px);
  z-index: 100;
  margin-top: -20px;

  :deep(.banner-section.is-fixed) + & {
    padding-top: $banner-fixed-height;
    z-index: 1;
  }
}

.content-layout {
  position: relative;
}

.content-row {
  position: relative;
}

.main-wrap {
  position: relative;
  background: #fff;
  border-radius: $border-radius-lg $border-radius-lg 0 0;
}

.content-body {
  position: relative;
  min-width: 0;
}

.home-sidebar-wrap {
  display: none;
}

.tag-list {
  display: flex;
  padding: 10px 0;
  height: 55px;
  justify-content: space-between;
  box-shadow: $shadow-sm;
  .tag-list-container {
    position: relative;
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
    padding: 0 10px;
  }
  // :deep(.swiper) {
  //   overflow: visible !important;
  // }
  .tag-swiper {
    width: 100%;

    :deep(.swiper-wrapper) {
      justify-content: space-between;
    }
  }
  .custom-pagination {
    display: flex;
    justify-content: center;
    bottom: -8px;
    :deep(.swiper-pagination-bullet) {
      width: 6px;
      height: 6px;
      background: #ddd;
      opacity: 0.6;
      margin: 0 3px;
      transition: all 0.3s ease;
    }
    :deep(.swiper-pagination-bullet-active) {
      background: $primary-color;
      opacity: 1;
    }
  }
  .tag-item {
    position: relative;
    cursor: pointer;
    text-align: center;
    font-size: 14px;
    color: $text-color;
    padding: 4px 0;
    width: auto !important;
    margin: 0 10px;
    white-space: nowrap;
    &.active {
      font-weight: 600;
      color: $primary-color;
      font-size: 16px;
      top: -2px;
      &::after {
        content: "";
        position: absolute;
        bottom: -2px;
        left: 50%;
        transform: translateX(-50%);
        width: 25px;
        background: $primary-color;
        border-radius: 3px;
        height: 3px;
      }
    }
  }

  &.is-fixed {
    position: fixed;
    top: $banner-fixed-height;
    left: 0;
    right: 0;
    background: transparent;
    z-index: 1000;
    // 白底用 fixed 相对视口，保证铺满整屏宽
    &::before {
      content: "";
      position: fixed;
      top: $banner-fixed-height;
      left: 0;
      width: 100vw;
      height: 55px;
      background: $white;
      z-index: -1;
      pointer-events: none;
    }
  }
}

// 下拉刷新指示器样式
.pull-refresh-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: transparent;
  transition: height 0.3s ease, top 0.3s ease;
  z-index: 1;
  pointer-events: none;

  .spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid $primary-color;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

.content-list {
  min-height: calc(100vh - 180px);

  padding: 10px $spacing-base 150px;
  position: relative;
}

.loading-wrapper,
.empty-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  transition: opacity 0.3s ease;
}

.load-more-trigger {
  height: 1px;
}

.sort-selector {
  max-width: 800px;
  margin: 0 auto;
  background: $white;
  border-radius: $border-radius-sm;
  margin-bottom: 10px;
  padding: 12px $spacing-base;
  box-shadow: $shadow-sm;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .current-section {
    font-size: 14px;
    font-weight: 600;
    color: $text-color;

    span {
      white-space: nowrap;
    }
  }

  .sort-options {
    display: flex;
    gap: 10px;
  }

  .sort-option {
    padding: 6px 12px;
    border-radius: 16px;
    background: #f5f5f5;
    cursor: pointer;
    font-size: 12px;
    color: #666;

    &:active {
      transform: scale(0.95);
    }

    &.active {
      background: $primary-color;
      color: $white;
      font-weight: 500;
    }

    span {
      white-space: nowrap;
    }
  }
}

.tag-selector {
  max-width: 800px;
  margin: 0 auto;
  background: $white;
  border-radius: $border-radius-sm;
  margin-bottom: 10px;
  padding: 12px $spacing-base;
  box-shadow: $shadow-sm;
}

.load-more-loading,
.load-more-tip,
.load-more-end {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  color: #999;
  font-size: 14px;

  span {
    margin-left: 8px;
  }
}

.load-more-tip {
  color: #666;

  span {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: -20px;
      top: 50%;
      transform: translateY(-50%);
      width: 12px;
      height: 12px;
      border: 2px solid #ddd;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }
}

.load-more-end {
  color: #ccc;
  font-size: 12px;
}

@keyframes spin {
  0% {
    transform: translateY(-50%) rotate(0deg);
  }
  100% {
    transform: translateY(-50%) rotate(360deg);
  }
}

.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .empty-img {
    width: 150px;
    height: 150px;
    object-fit: contain;
  }

  .empty-tip {
    font-size: 14px;
    color: #999;
  }
}

.fab-btn {
  position: fixed;
  right: 10px;
  bottom: 90px;
  width: 64px;
  height: 64px;
  background: #ffbe23;
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: pointer;
  transition: box-shadow 0.2s;
  img {
    width: 40px;
    height: 40px;
    pointer-events: none;
  }
  &:active {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }
}
@media (min-width: 1024px) {
  .sort-selector .sort-option {
    font-size: 14px;
  }

  .content-layout {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 20px;
    padding-top: 10px;
  }

  .content-row {
    display: flex;
    flex-direction: column;
    flex: 0 0 800px;
    width: 800px;
    max-width: 800px;
  }
  .content-list {
    padding: 0;
  }

  .sort-selector,
  .tag-selector {
    box-shadow: none;
    border-radius: 0;
  }

  .home-sidebar-wrap {
    display: flex;
    flex-shrink: 0;
  }

  .tag-list {
    box-shadow: none;
    border-top: 1px solid #f0f0f0;
  }

  .tag-list.is-fixed {
    left: calc(50vw - #{$pc-content-block-half});
    right: auto;
    width: 800px;
  }
}
</style>
